import { createI18n } from 'vue-i18n'
import { compile } from '@intlify/core-base'
import { get } from 'lodash'

const numberFormats = {
  nl: {
    currency: {
      style: 'currency',
      currency: 'EUR',
      currencyDisplay: 'symbol',
    },
    currencyRounded: {
      style: 'currency',
      currency: 'EUR',
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    },
    number: {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  },
}

const datetimeFormats = {
  nl: {
    short: {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    },
  },
}

/*
 * Labels come from the dictionary_item table and are written by people, not by
 * developers, so a label can easily contain something vue-i18n reads as syntax:
 * an `@` starts a linked message, `{{` a nested placeholder. vue-i18n 9 rendered
 * such a message as-is; from v10 the compiler runs JIT and throws instead, which
 * would take down whatever component asked for the label.
 *
 * Falling back to the raw text keeps the v9 behaviour. Verified against all 1656
 * shared-brand labels on ACC: every one renders identically on 9.14.5 and 11.4.8
 * with this in place, and two of them (`/settings/email/email from address` and
 * `/pdf/pricelist page/pricelist range value wrapper`) throw without it.
 */
const tolerantMessageCompiler = (message, context) => {
  try {
    return compile(message, context)
  } catch (e) {
    console.error(`label '${context.key}' is not a valid message, using it verbatim`, e)
    return () => (typeof message === 'string' ? message : context.key)
  }
}

const CustomVueI18n = createI18n({
  locale: 'nl', // set locale
  numberFormats,
  datetimeFormats,
  // Composition API mode. `allowComposition` used to sit here too; it only
  // ever affected legacy mode and vue-i18n 10 removed the option entirely.
  legacy: false,
  warnHtmlMessage: false,
  missingWarn: false,
  messageResolver: (obj, path) => get(obj, path, '')?.replaceAll(/\|/g, "{'|'}") || null,
  messageCompiler: tolerantMessageCompiler,
})

CustomVueI18n.global.OriginalTranslate = CustomVueI18n.global.t
CustomVueI18n.global.t = (key, ...values) => {
  return CustomVueI18n.global.OriginalTranslate(key.toLowerCase(), ...values)
}

CustomVueI18n.OriginalNumberFormat = CustomVueI18n.global.n
CustomVueI18n.global.n = (number, ...args) => {
  let numberValue
  try {
    if (!!number) {
      numberValue = Number(number)
    } else {
      numberValue = Number()
    }
  } catch {
    let error = SyntaxError(`incorrect number format, value provided: ${number}`)
    console.error(error)
    numberValue = Number()
  }

  // vue-i18n 9 threw on a non-finite value and the catch below turned that into
  // a formatted zero. From v10 it logs and returns an empty string instead, so
  // the fallback has to be reached by checking the value rather than by
  // catching.
  if (!Number.isFinite(numberValue)) {
    console.error(SyntaxError(`incorrect number format, value provided: ${number}`))
    numberValue = Number()
  }

  let result
  try {
    result = CustomVueI18n.OriginalNumberFormat(numberValue, ...args)
  } catch {
    let error = SyntaxError(
      `incorrect number format, vue failed to format, value provided: ${number}`
    )
    console.error(error)

    result = CustomVueI18n.OriginalNumberFormat(Number(), ...args)
  }
  return result
}

CustomVueI18n.global.translationExists = (key, ...values) => {
  let translation = CustomVueI18n.global.t(key, ...values)
  return !!translation && translation?.toLowerCase() != key?.toLowerCase()
}

export default CustomVueI18n
